import React, { memo, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { collection, onSnapshot } from 'firebase/firestore';
import { FiAlertCircle, FiChevronRight, FiDownload, FiFileText, FiPaperclip, FiPlus, FiSearch } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { useSessionUser } from '../../lib/permissions';
import { formatDate, yearsSince } from '../../lib/format';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { BUTTON, CARD, EmptyState, INPUT, PageHeader, Pill, Segmented, SIZE, SkeletonRows } from '../ui/kit';
import { birthDateOf, initials } from '../patients/patientModel';
import { TYPE_OPTIONS, croAccess, searchText, typeLabel } from './croModel';

const PAGE_SIZE = 30;

// Same columns as the former export.
const exportExcel = (entries) => {
  const rows = entries.map(({ cro, patient }) => {
    const years = patient ? yearsSince(birthDateOf(patient)) : null;
    return {
      'Numéro de dossier': patient ? patient.dossierNumber : '',
      Patient: patient ? patient.nom : '',
      Chirurgiens: cro.chirurgiens,
      Anesthésistes: cro.anesthesistes,
      Diagnostic: cro.diagnos,
      'Indication Opératoire': cro.indicationOperatoire,
      Commentaire: String(cro.comment || '').trim(),
      Âge: years !== null ? years : patient ? patient.age : '',
      Genre: patient ? patient.genre : '',
      'Groupe sanguin': patient ? patient.groupeSanguin : '',
      'Numéro de téléphone': patient ? patient.numeroDeTelephone : '',
      'Adresse domicile': patient ? patient.addressDomicile : '',
      Date: formatDate(cro.datePatient),
      'Responsables CEC': cro.cecManagers,
      Instrumentistes: cro.instrumentalists,
    };
  });
  const sheet = XLSX.utils.json_to_sheet(rows);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'CRO');
  XLSX.writeFile(book, 'comptes_rendus_operatoires.xlsx');
};

const CroRow = memo(({ entry }) => {
  const { cro, patient } = entry;
  const files = (Array.isArray(cro.dropboxLinks) ? cro.dropboxLinks.length : 0) + (Array.isArray(cro.documents) ? cro.documents.length : 0);
  const name = patient ? patient.nom : 'Dossier introuvable';
  return (
    <li className="border-0 border-b-[1px] border-solid border-black/[0.05] last:border-b-0">
      <Link to={`/cro/${cro.id}`} className="flex items-center gap-[14px] px-[16px] py-[14px] text-ink no-underline transition hover:bg-black/[0.02] hover:text-ink sm:px-6">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-orange-50 text-[13px] font-semibold text-orange-600" aria-hidden="true">
          {initials(name)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-semibold">{name}</span>
          <span className="mt-0.5 block truncate text-[13px] text-black/45">
            {[entry.dateLabel || 'Date non précisée', patient && patient.dossierNumber && `Dossier ${patient.dossierNumber}`, cro.chirurgiens].filter(Boolean).join(' · ')}
          </span>
          {(cro.indicationOperatoire || cro.diagnos) && (
            <span className="mt-0.5 block truncate text-[12.5px] text-black/40">{[cro.indicationOperatoire, cro.diagnos].filter(Boolean).join(' · ')}</span>
          )}
        </span>
        <span className="hidden flex-none items-center gap-1.5 md:flex">
          <Pill tone={typeLabel(cro) === 'Normal' ? 'neutral' : 'brand'}>{typeLabel(cro)}</Pill>
          {files > 0 && (
            <Pill>
              <FiPaperclip size={11} aria-hidden="true" />
              {files}
            </Pill>
          )}
        </span>
        <FiChevronRight size={17} aria-hidden="true" className="flex-none text-black/25" />
      </Link>
    </li>
  );
});

const CroList = () => {
  const user = useSessionUser();
  const access = croAccess(user);
  const { patients, ensurePatients } = usePatientDoctorContext();
  const [cros, setCros] = useState([]);
  const [status, setStatus] = useState('loading');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [unit, setUnit] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);

  useEffect(() => {
    ensurePatients().catch(() => {});
    return onSnapshot(
      collection(db, 'cros'),
      (snapshot) => {
        setCros(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setStatus('ready');
      },
      () => setStatus('error')
    );
  }, [ensurePatients]);

  const patientsById = useMemo(() => Object.fromEntries(patients.map((patient) => [patient._id, patient])), [patients]);
  const prepared = useMemo(
    () =>
      cros
        .filter((cro) => access.visible(cro))
        .map((cro) => {
          const patient = patientsById[cro.patientId];
          return {
            cro,
            patient,
            time: new Date(cro.datePatient || 0).getTime() || 0,
            dateLabel: formatDate(cro.datePatient),
            type: TYPE_OPTIONS.some((option) => option.value === cro.commentType) ? cro.commentType : 'Autres',
            search: searchText(cro, patient),
          };
        })
        .sort((a, b) => b.time - a.time),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cros, patientsById, user.role, user.service]
  );
  const unitsInData = useMemo(() => [...new Set(prepared.map((entry) => entry.cro.service).filter(Boolean))].sort(), [prepared]);

  const query = useDeferredValue(search.trim().toLowerCase());
  const shown = useMemo(
    () =>
      prepared.filter(
        (entry) =>
          (!type || entry.type === type) && (!unit || entry.cro.service === unit) && (!query || query.split(/\s+/).every((word) => entry.search.includes(word)))
      ),
    [prepared, type, unit, query]
  );
  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [type, unit, query]);

  const chip = (value, label) => (
    <button
      key={value || 'all'}
      type="button"
      aria-pressed={type === value}
      onClick={() => setType(value)}
      className={`inline-flex h-9 flex-none cursor-pointer items-center whitespace-nowrap rounded-full border-0 px-[14px] text-[13.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 ${
        type === value ? 'bg-ink text-white' : 'bg-white text-black/65 shadow-[0_1px_2px_rgba(0,0,0,0.06)] hover:text-ink'
      }`}
    >
      {label}
    </button>
  );

  return (
    <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader
        eyebrow="Soins"
        title="Comptes rendus opératoires"
        subtitle={
          status === 'ready'
            ? `${shown.length} compte${shown.length > 1 ? 's' : ''} rendu${shown.length > 1 ? 's' : ''} · ${access.isAdmin ? 'toutes les unités' : `unité ${user.service || 'non précisée'}`}`
            : 'Chargement des comptes rendus…'
        }
        actions={
          <>
            {access.canExport && (
              <button type="button" onClick={() => exportExcel(shown)} disabled={!shown.length} className={`${BUTTON.secondary} ${SIZE.md}`}>
                <FiDownload size={15} aria-hidden="true" />
                Excel
              </button>
            )}
            {access.canCreate && (
              <Link to="/cro/nouveau" className={`${BUTTON.primary} ${SIZE.md}`}>
                <FiPlus size={16} aria-hidden="true" />
                Nouveau CRO
              </Link>
            )}
          </>
        }
      />

      <div className="mt-8 flex flex-col gap-[12px]">
        <div className="relative">
          <FiSearch size={17} aria-hidden="true" className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-black/35" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Patient, n° de dossier, chirurgien, diagnostic, indication…"
            aria-label="Rechercher un compte rendu"
            className={`${INPUT} h-11 pl-[42px]`}
          />
        </div>
        <div className="flex flex-col gap-[12px] lg:flex-row lg:items-center lg:justify-between">
          <div className="-mx-[20px] flex gap-2 overflow-x-auto px-[20px] pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden">
            {chip('', 'Tous')}
            {TYPE_OPTIONS.map((option) => chip(option.value, option.value === 'Autres' ? 'Autres' : option.label))}
          </div>
          {unitsInData.length > 1 && (
            <Segmented value={unit} onChange={setUnit} allowEmpty={false} options={[{ value: '', label: 'Toutes les unités' }, ...unitsInData.map((name) => ({ value: name, label: name }))]} />
          )}
        </div>
      </div>

      <section className={`${CARD} mt-[20px] overflow-hidden`}>
        {status === 'loading' ? (
          <div className="p-6">
            <SkeletonRows rows={6} />
          </div>
        ) : status === 'error' ? (
          <EmptyState icon={FiAlertCircle} title="Comptes rendus indisponibles">
            Ils n’ont pas pu être chargés. Vérifiez la connexion, puis rechargez la page.
          </EmptyState>
        ) : shown.length === 0 ? (
          <EmptyState icon={FiFileText} title="Aucun compte rendu">
            {prepared.length ? 'Aucun compte rendu ne correspond à ces filtres.' : 'Les comptes rendus opératoires de votre unité apparaîtront ici.'}
          </EmptyState>
        ) : (
          <>
            <ul className="m-0 list-none p-0">
              {shown.slice(0, limit).map((entry) => (
                <CroRow key={entry.cro.id} entry={entry} />
              ))}
            </ul>
            {limit < shown.length && (
              <div className="flex flex-col items-center gap-1 border-0 border-t-[1px] border-solid border-black/[0.05] p-[16px] text-center">
                <button type="button" onClick={() => setLimit((count) => count + PAGE_SIZE)} className={`${BUTTON.ghost} ${SIZE.md}`}>
                  Afficher {Math.min(PAGE_SIZE, shown.length - limit)} comptes rendus de plus
                </button>
                <span className="text-[12.5px] text-black/40">
                  {limit} sur {shown.length} affichés
                </span>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
};

export default CroList;
